import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-minute-stepper',
    templateUrl: './minute-stepper.component.html',
    styleUrls: ['./minute-stepper.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MinuteStepperComponent),
            multi: true
        }
    ],
    standalone: false
})
export class MinuteStepperComponent implements ControlValueAccessor {
  @Input() min = 1;
  @Input() max = 24 * 60; // default max 24 hours in minutes

  valueMinutes = 0;
  private debounceTimeout: any;

  private onTouched: () => void = () => { };
  private onChange: (v: any) => void = () => { };

  writeValue(obj: any): void {
    if (obj == null || obj === '') {
      this.valueMinutes = 0;
      return;
    }

    // Accept either minutes (number) or time string HH:MM:SS
    if (typeof obj === 'number') {
      this.valueMinutes = obj;
    } else if (typeof obj === 'string') {
      const parts = obj.split(':').map(p => Number(p));
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        this.valueMinutes = (parts[0] || 0) * 60 + (parts[1] || 0);
      } else {
        const n = Number(obj);
        this.valueMinutes = isNaN(n) ? 0 : n;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // no-op for now
  }

  decrement(): void {
    if (this.valueMinutes <= this.min) {
      return;
    }
    this.setMinutes(this.valueMinutes - 1);
  }

  increment(): void {
    if (this.valueMinutes >= 60) {
      return;
    }
    this.setMinutes(this.valueMinutes + 1);
  }

  setMinutes(mins: any): void {
    // Convert input to number
    let val = Number(mins);
    // Handle invalid inputs
    if (isNaN(val)) val = this.min;
    
    // Clear any existing debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    // Temporarily accept the value without min/max clamping so user can type freely
    this.valueMinutes = Math.floor(val);
    this.emitCurrentValue();
    
    // Enforce the constraints after a debounce
    this.debounceTimeout = setTimeout(() => {
      if (this.valueMinutes < this.min) {
        this.valueMinutes = this.min;
        this.emitCurrentValue();
      } else if (this.valueMinutes > this.max) {
        this.valueMinutes = this.max;
        this.emitCurrentValue();
      }
    }, 500);
  }

  private emitCurrentValue(): void {
    // Prepare the HH:MM:SS string
    const hh = Math.floor(this.valueMinutes / 60).toString().padStart(2, '0');
    const mm = (this.valueMinutes % 60).toString().padStart(2, '0');
    const ss = '00';
    const out = `${hh}:${mm}:${ss}`;

    // IMPORTANT: Notify the Parent Form
    this.onChange(out);
    this.onTouched();
  }
}
